import { apiBaseUrl } from './api-url.js';
import { currentAccessToken } from './auth.js';
import { ledger } from './browser.js';

/**
 * Talking to the import routes from the browser.
 *
 * The same shape as `platform/export.ts`, and for the same reason: these are plain
 * authenticated HTTP routes, so the app has to fetch with a bearer token rather than
 * point a link at them. What differs is the direction — the file goes UP.
 *
 * R1 — THE FILE IS SENT TWICE, ONCE PER STEP, and never stored anywhere. Preview and
 * apply are independent requests carrying the same bytes; there is no server-side
 * session that could describe a different file than the one applied. The browser
 * already holds the file, so re-sending ~1 MB costs nothing worth designing around.
 *
 * FAILURES ARE NEVER SILENT (G10): every path returns a message the caller renders
 * and writes a ledger entry. An import that did not happen must not be
 * indistinguishable from a slow browser.
 */

// Hoisted to api-url.ts in 0.1.209 - this was one of four copies of the same fact.
const API_URL = apiBaseUrl();
/** Generous: apply writes thousands of rows inside one transaction. The server's own
 * timeout is 120s, so the client waits slightly longer rather than abandoning a
 * request the server is still honouring. */
const IMPORT_TIMEOUT_MS = 150_000;

export interface ImportProfileSummary {
  name: string;
  label: string;
  delimiter: string;
  identityColumn: string;
}

export interface ImportReport {
  ok: true;
  preview: boolean;
  profile: string;
  profileLabel: string;
  /** How the profile was chosen, in words the reader can check against their file. */
  detectedBy: string;
  rowsInFile: number;
  plan: {
    itemsToWrite: number;
    itemsSkippedExisting: number;
    strayImported: { externalRef: string; name: string }[];
    strayLocalCount: number;
    codesSkipped: { externalRef: string; code: string }[];
    categoriesToCreate: string[];
    locationsToCreate: string[];
    fieldsToCreate: { key: string; label: string; kind: string; options: string[] }[];
    custodyRows: number;
    /** Clients the file names, with how many garments each had out. NO booking is made. */
    clientsToCreate: { name: string; email: string | null; company: string | null; itemCount: number }[];
    clientsExisting: number;
    flaggedRows: number;
    photosToLink: number;
    verified?: { items: number; photos: number; codes: number; fieldValues: number; categories: number };
  };
  /** Present when migration 018 is applied — the handle undo and photo fetching need. */
  importRunId?: string | null;
  /** Items imported WITHOUT their custody — the custodian is not a user here. */
  custodySkipped?: string[];
  rejects: { rowNumber: number; externalRef: string; column: string; value: string; reason: string }[];
  unmapped: { column: string; count: number; examples: string[] }[];
  emptyFields: string[];
}

export type ImportOutcome = ImportReport | { ok: false; message: string; detail?: string[] };

export interface UndoOutcome {
  ok: true;
  removed: number;
  kept: { id: string; name: string; reason: string }[];
}

/** One import file's outstanding photographs. `runId` null = items belonging to no file. */
export interface PhotoRun {
  runId: string | null;
  filename: string | null;
  importedAt: string | null;
  total: number;
  fetched: number;
  pending: number;
}

export interface PhotoOutcome {
  ok: true;
  fetched: number;
  failed: number;
  refused: number;
  total: number;
  pending: number;
  /** Optional: an API one release behind does not send it, and the screen falls back. */
  runs?: PhotoRun[];
}

export type ActionOutcome<T> = T | { ok: false; message: string; detail?: string[] };

export async function listImportProfiles(): Promise<ImportProfileSummary[]> {
  if (!API_URL) return [];
  try {
    const response = await fetch(`${API_URL}/import/profiles`, { signal: AbortSignal.timeout(15_000) });
    if (!response.ok) return [];
    const body = (await response.json()) as { profiles?: ImportProfileSummary[] };
    return body.profiles ?? [];
  } catch (error) {
    ledger.record(error, 'import:profiles', { notified: false });
    return [];
  }
}

/**
 * A POST with no body, for the acts that take their argument in the query string.
 * Shares the refusal shape with `send` below — the routes answer failures the same way,
 * and two copies of the classify block is how one stops matching the other.
 */
async function post<T>(path: string, source: string): Promise<ActionOutcome<T>> {
  if (!API_URL) return { ok: false, message: 'This build has no API configured.' };
  const token = await currentAccessToken();
  if (!token) return { ok: false, message: 'Your session has expired. Sign in again, then try that.' };

  try {
    const response = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}` },
      signal: AbortSignal.timeout(IMPORT_TIMEOUT_MS),
    });
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    if (!response.ok) {
      const message = typeof body.reason === 'string' ? body.reason : `That failed (HTTP ${String(response.status)}).`;
      const detail = Array.isArray(body.detail) ? body.detail.filter((d): d is string => typeof d === 'string') : undefined;
      ledger.record(Object.assign(new Error(message), { status: response.status }), source, {
        notified: true,
        classify: { httpStatus: response.status },
      });
      return detail ? { ok: false, message, detail } : { ok: false, message };
    }
    return body as T;
  } catch (error) {
    ledger.record(error, source, { notified: true });
    return { ok: false, message: 'Could not reach the server. Check the connection and try again.' };
  }
}

/** Reverse one import run. Admin only; the server checks. */
export async function undoImport(runId: string): Promise<ActionOutcome<UndoOutcome>> {
  return post<UndoOutcome>(`/import/undo?run=${encodeURIComponent(runId)}`, 'import:undo');
}

/** Fetch one bounded batch of the photos an import linked, and report progress. */
/**
 * ONE SMALL BATCH. 50, not the route's 500, and the number is load-bearing.
 *
 * Field report, 30 Jul 2026: "fetch photos doesn't seem to do anything", with
 * `SERVER_ERROR (import:photos)` in the ledger. The server log tells the rest: a 500-photo
 * batch took **2 minutes 13 seconds** and succeeded — but the route's own timeout is 120
 * seconds, so the CLIENT was handed a TimeoutError at two minutes while the work carried on
 * and logged success afterwards. The photos really were arriving; the answer never did.
 *
 * At 50 a batch landed in roughly thirteen seconds: comfortably inside the timeout, and an
 * interruption cost seconds rather than minutes.
 *
 * ⚠️ LOWERED TO 10 ON 1 Aug 2026, at the atelier's request — thirteen seconds between
 * movements is still long enough to read as stuck when you are watching it. At 10 the bar
 * moves roughly every three seconds and ~190 times over the imported catalogue, which is
 * the difference between "working" and "frozen" to somebody standing at the rack.
 *
 * The cost is more round trips, and it is small: each request is short, the work is
 * idempotent by `fetched_at`, and the loop stops the moment a round stops reducing
 * `pending`. Going further down would start paying more in request overhead than it buys
 * in reassurance.
 */
export const PHOTO_FETCH_BATCH = 10;

export async function fetchImportPhotos(runId?: string | null): Promise<ActionOutcome<PhotoOutcome>> {
  // The run rides in the query string so a fetch can be scoped to ONE uploaded file. Absent
  // = every outstanding photograph, which is what the headline button still does.
  const scope = runId ? `&runId=${encodeURIComponent(runId)}` : '';
  return post<PhotoOutcome>(`/import/photos?limit=${String(PHOTO_FETCH_BATCH)}${scope}`, 'import:photos');
}

/**
 * How much of the photo fetch is left, asked of the SERVER rather than remembered.
 *
 * Field report, 30 Jul 2026: "when I click see items and go back to import it reset the
 * import screen, so I cannot click fetch photos anymore." The button was gated on the apply
 * report held in component state, which the unmount threw away — so the work was still
 * outstanding and the only control for it had vanished.
 *
 * Caching that report would have fixed the symptom and kept the lie: the truth about what
 * is left lives in `item_photos` (`source_url` set, `fetched_at` null) and the route to ask
 * it already existed. A screen that asks is also right after a reload, on another device,
 * and after a fetch somebody else ran.
 */
export async function importPhotoProgress(): Promise<
  ActionOutcome<{ ok: true; total: number; fetched: number; pending: number; runs: PhotoRun[] }>
> {
  if (!API_URL) return { ok: false, message: 'This build has no API configured.' };
  const token = await currentAccessToken();
  if (!token) return { ok: false, message: 'Your session has expired. Sign in again.' };
  try {
    const response = await fetch(`${API_URL}/import/photos`, {
      headers: { authorization: `Bearer ${token}` },
    });
    const body = (await response.json()) as {
      ok?: boolean;
      total?: number;
      fetched?: number;
      pending?: number;
      runs?: PhotoRun[];
      reason?: string;
    };
    if (!response.ok || body.ok !== true) {
      return { ok: false, message: body.reason ?? `The server refused (${String(response.status)}).` };
    }
    // `runs` defaults to empty rather than undefined: an API one release behind sends no
    // breakdown, and the screen must fall back to its single total rather than render a
    // per-file list it cannot fill. The bundle reaches Cloudflare before the API reaches
    // Fly on every release, so that window is real.
    return {
      ok: true,
      total: body.total ?? 0,
      fetched: body.fetched ?? 0,
      pending: body.pending ?? 0,
      runs: body.runs ?? [],
    };
  } catch {
    // Quiet by design: this runs on mount and only decides whether a button is offered.
    // An alert here would make opening the screen look like a failure.
    return { ok: false, message: 'Could not reach the server.' };
  }
}

/**
 * One function for both steps — they differ only in the path, and two copies of an
 * upload-and-classify block is how one of them stops matching the other.
 */
async function send(step: 'preview' | 'apply', profile: string | null, file: File): Promise<ImportOutcome> {
  if (!API_URL) return { ok: false, message: 'This build has no API configured.' };

  const token = await currentAccessToken();
  if (!token) {
    return { ok: false, message: 'Your session has expired. Sign in again, then try the import.' };
  }

  try {
    const query = profile === null ? '' : `?profile=${encodeURIComponent(profile)}`;
    const response = await fetch(`${API_URL}/import/${step}${query}`, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}`, 'content-type': 'text/csv' },
      body: file,
      signal: AbortSignal.timeout(IMPORT_TIMEOUT_MS),
    });

    if (!response.ok) {
      // The routes answer failures as JSON with a `reason` written for a human, and
      // 422 carries `detail` lines naming the rows. Use them rather than inventing a
      // message from the status code.
      let message = `The import failed (HTTP ${String(response.status)}).`;
      let detail: string[] | undefined;
      try {
        const body = (await response.json()) as { reason?: unknown; detail?: unknown };
        if (typeof body.reason === 'string') message = body.reason;
        if (Array.isArray(body.detail)) detail = body.detail.filter((d): d is string => typeof d === 'string');
      } catch {
        /* not JSON — keep the status-based line */
      }
      ledger.record(Object.assign(new Error(message), { status: response.status, name: 'ImportRefused' }), `import:${step}`, {
        notified: true,
      });
      return { ok: false, message, ...(detail ? { detail } : {}) };
    }

    return (await response.json()) as ImportReport;
  } catch (error) {
    // AbortError/TimeoutError are retryable and say so; anything else is recorded the
    // same way rather than swallowed.
    ledger.record(error, `import:${step}`, { notified: true });
    const timedOut = error instanceof Error && (error.name === 'TimeoutError' || error.name === 'AbortError');
    return {
      ok: false,
      message: timedOut
        ? 'The import took too long and was stopped. Nothing was changed — try again, or use a smaller file.'
        : 'The import could not reach the server. Nothing was changed.',
    };
  }
}

/** No profile: the server recognises the file from its header and says how. */
export const previewImport = (file: File): Promise<ImportOutcome> => send('preview', null, file);
export const applyImport = (file: File): Promise<ImportOutcome> => send('apply', null, file);
